import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import translationEN from './locales/en.json';

export const SUPPORTED_LANGUAGES = ['en', 'hi', 'gu', 'mr'];
const STORAGE_KEY = 'ayodhyadham_language';

/**
 * Only English ships in the main bundle. The other locales are fetched on
 * demand so a reader of one language never downloads the other three
 * (they are ~20–24 kB gzip each).
 */
const loaders = {
  hi: () => import(/* webpackChunkName: "locale-hi" */ './locales/hi.json'),
  gu: () => import(/* webpackChunkName: "locale-gu" */ './locales/gu.json'),
  mr: () => import(/* webpackChunkName: "locale-mr" */ './locales/mr.json'),
};

const loaded = new Set(['en']);

const savedLanguage = (() => {
  if (typeof window === 'undefined') return 'en';
  // ?lang= wins over the stored choice so the hreflang alternates advertised in
  // the sitemap resolve to the language they promise, and language-specific
  // links can be shared.
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (SUPPORTED_LANGUAGES.includes(fromUrl)) return fromUrl;
  const stored = localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_LANGUAGES.includes(stored) ? stored : 'en';
})();

i18n.use(initReactI18next).init({
  resources: { en: { translation: translationEN } },
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: SUPPORTED_LANGUAGES,
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

/** Fetch a locale bundle once and register it with i18next. */
export async function loadLanguage(lng) {
  if (loaded.has(lng) || !loaders[lng]) return;
  try {
    const mod = await loaders[lng]();
    i18n.addResourceBundle(lng, 'translation', mod.default || mod, true, true);
    loaded.add(lng);
  } catch (err) {
    // Keep the current language rather than switching to an empty bundle.
    console.error(`Failed to load the "${lng}" translations`, err);
  }
}

/** Load the bundle first, then switch, so the UI never flashes half-translated. */
export async function changeAppLanguage(lng) {
  if (!SUPPORTED_LANGUAGES.includes(lng)) return;
  await loadLanguage(lng);
  await i18n.changeLanguage(lng);
}

/**
 * Resolves once the reader's saved language is loaded and active.
 * `src/index.js` awaits this before the first render so the page never paints
 * half-translated and <html lang> is correct from the start.
 */
export const i18nReady = savedLanguage === 'en'
  ? Promise.resolve()
  : changeAppLanguage(savedLanguage);

i18n.on('languageChanged', (lng) => {
  if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, lng);
});

export default i18n;
